import type { Paginated, TaskListItem } from '@erp/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { useState } from 'react';
import { api } from '@/lib/api';
import { type TasksFilter, tasksQuery } from '@/lib/queries';
import { TaskCard } from './tasks';

/**
 * Вид «Сроки»: незакрытые задачи по колонкам «Просрочены / Сегодня / На этой неделе /
 * На следующей неделе / Позже / Без срока». Перетаскивание в колонку ставит новый срок.
 */

type Bucket = 'overdue' | 'today' | 'week' | 'next' | 'later' | 'none';

const COLUMNS: { bucket: Bucket; title: string; hint?: string; droppable: boolean; tone: string }[] = [
  { bucket: 'overdue', title: 'Просрочены', droppable: false, tone: 'bg-danger' },
  { bucket: 'today', title: 'Сегодня', hint: 'срок — сегодня', droppable: true, tone: 'bg-signal' },
  { bucket: 'week', title: 'На этой неделе', hint: 'срок — пятница', droppable: true, tone: 'bg-link' },
  {
    bucket: 'next',
    title: 'На следующей неделе',
    hint: 'срок — пятница следующей недели',
    droppable: true,
    tone: 'bg-steel',
  },
  { bucket: 'later', title: 'Позже', droppable: false, tone: 'bg-line-strong' },
  { bucket: 'none', title: 'Без срока', hint: 'снять срок', droppable: true, tone: 'bg-line-strong' },
];

const DEFAULT_HOUR = 18;

function startOfDay(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/** Понедельник следующей недели 00:00 (по времени браузера — у сотрудников московское). */
function nextMonday(now: Date) {
  const d = startOfDay(now);
  const wd = d.getDay() || 7;
  d.setDate(d.getDate() + (8 - wd));
  return d;
}

export function bucketOf(task: Pick<TaskListItem, 'deadline'>, now = new Date()): Bucket {
  if (!task.deadline) return 'none';
  const d = new Date(task.deadline);
  if (d.getTime() < now.getTime()) return 'overdue';
  const tomorrow = startOfDay(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  if (d < tomorrow) return 'today';
  const monday = nextMonday(now);
  if (d < monday) return 'week';
  const after = new Date(monday);
  after.setDate(after.getDate() + 7);
  if (d < after) return 'next';
  return 'later';
}

/** Новый срок для колонки. Время дня сохраняем от старого срока, иначе 18:00. */
export function deadlineFor(bucket: Bucket, current: string | null, now = new Date()): Date | null {
  if (bucket === 'none') return null;
  const old = current ? new Date(current) : null;
  const at = (day: Date) => {
    const d = new Date(day);
    d.setHours(old ? old.getHours() : DEFAULT_HOUR, old ? old.getMinutes() : 0, 0, 0);
    return d;
  };
  const today = startOfDay(now);
  if (bucket === 'today') {
    const d = at(today);
    // Время уже прошло — до конца дня, чтобы задача не стала просроченной сразу
    if (d.getTime() <= now.getTime()) d.setHours(23, 59, 0, 0);
    return d;
  }
  const monday = nextMonday(now);
  const friday = new Date(monday);
  friday.setDate(friday.getDate() - 3);
  if (bucket === 'week') {
    // Пятница этой недели; если она уже прошла (выходные) — воскресенье
    const target = friday > today ? friday : new Date(monday.getTime() - 86_400_000);
    return target > today ? at(target) : deadlineFor('today', current, now);
  }
  const nextFriday = new Date(monday);
  nextFriday.setDate(nextFriday.getDate() + 4);
  return at(nextFriday);
}

export function DeadlineBoard({ items, filter }: { items: TaskListItem[]; filter: TasksFilter }) {
  const qc = useQueryClient();
  const [dragId, setDragId] = useState<string | null>(null);
  const [over, setOver] = useState<Bucket | null>(null);
  const [error, setError] = useState<string | null>(null);
  const key = tasksQuery(filter).queryKey;
  const now = new Date();

  const change = useMutation({
    mutationFn: (v: { id: string; deadline: string | null }) =>
      api(`/tasks/${v.id}`, { method: 'PATCH', body: { deadline: v.deadline } }),
    onError: (err) => {
      setError(
        err.message === 'Изменять задачу может постановщик'
          ? 'Срок меняет постановщик задачи — попросите его или напишите в комментарии'
          : err.message,
      );
      window.setTimeout(() => setError(null), 6000);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['tasks'] }),
  });

  function drop(bucket: Bucket) {
    const task = items.find((t) => t.id === dragId);
    setDragId(null);
    setOver(null);
    if (!task || bucketOf(task, now) === bucket) return;
    const deadline = deadlineFor(bucket, task.deadline, now)?.toISOString() ?? null;
    qc.setQueryData(key, (old: Paginated<TaskListItem> | undefined) =>
      old
        ? {
            ...old,
            items: old.items.map((t) => (t.id === task.id ? { ...t, deadline, isOverdue: false } : t)),
          }
        : old,
    );
    change.mutate({ id: task.id, deadline });
  }

  const columns = COLUMNS.map((c) => ({
    ...c,
    items: items
      .filter((t) => bucketOf(t, now) === c.bucket)
      .sort((a, b) => (a.deadline ?? '').localeCompare(b.deadline ?? '') || b.number - a.number),
  }));

  return (
    <div>
      {error && (
        <div
          role="alert"
          className="mb-3 rounded-md border border-danger/30 bg-danger-soft px-3 py-2 text-[13.5px] text-danger"
        >
          {error}
        </div>
      )}
      <div className="-mx-4 overflow-x-auto px-4 pb-2 lg:-mx-8 lg:px-8">
        <div className="grid min-w-[1260px] grid-cols-6 gap-3">
          {columns.map((col) => (
            <section
              key={col.bucket}
              aria-label={col.title}
              onDragOver={(e) => {
                if (!dragId || !col.droppable) return;
                e.preventDefault();
                setOver(col.bucket);
              }}
              onDragLeave={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget as Node)) setOver(null);
              }}
              onDrop={(e) => {
                e.preventDefault();
                if (col.droppable) drop(col.bucket);
              }}
              className={clsx(
                'flex min-h-[320px] flex-col rounded-[var(--radius-panel)] bg-sunken/70 p-2',
                over === col.bucket && 'outline-2 outline-dashed outline-line-strong',
                dragId && !col.droppable && 'opacity-60',
              )}
            >
              <header className="px-1.5 pb-2 pt-1">
                <div className="flex items-center gap-2">
                  <span aria-hidden className={clsx('size-2 rounded-full', col.tone)} />
                  <h2 className="text-[13.5px] font-semibold">{col.title}</h2>
                  <span className="text-[13px] tabular-nums text-steel">{col.items.length}</span>
                </div>
                {dragId && col.hint && <p className="mt-0.5 text-[12px] text-steel">{col.hint}</p>}
              </header>
              <div className="flex flex-col gap-2">
                {col.items.map((t) => (
                  <TaskCard
                    key={t.id}
                    task={t}
                    dragging={dragId === t.id}
                    onDragStart={(e) => {
                      e.dataTransfer.effectAllowed = 'move';
                      e.dataTransfer.setData('text/plain', t.id);
                      setDragId(t.id);
                    }}
                    onDragEnd={() => {
                      setDragId(null);
                      setOver(null);
                    }}
                  />
                ))}
                {col.items.length === 0 && (
                  <p className="px-1.5 py-6 text-center text-[12.5px] text-steel">Пусто</p>
                )}
              </div>
            </section>
          ))}
        </div>
      </div>
      <p className="mt-2 text-[12.5px] text-steel">
        Перетащите задачу в «Сегодня», «На этой неделе» или «На следующей неделе» — срок поставится сам (время
        дня сохранится, без срока — 18:00). Менять срок может постановщик.
      </p>
    </div>
  );
}
